import {
  isUnackedUrgent,
  standingAlarmStatus,
  type QueueItem,
} from './anomalyQueue';
import { useWallQueueItems } from './wallQueueData';

/**
 * WALL hero count-chip strip (#268 + PART X Ruling 1 ops-seat condition).
 *
 * A COMPACT single-row strip pinned to a hero corner — the Part II §5
 * standing-alarm/queue-health surface's home on the v2 wall (the queue zone
 * left for the desks). Composition law: small chips, one row; floating CARDS
 * over the hero are forbidden at strip aspects (Ruling 2), so this is the
 * only standing hero chrome besides the identity tag.
 *
 *   • URGENT — #268 option (a), founder-ratified (provisional): counts
 *     UNACKED urgents only, via the queue's own shared predicate
 *     (`isUnackedUrgent`: Response Level ≥3, not acknowledged) — the chip,
 *     the ticker takeover and the desk queue can never disagree. Acked reds
 *     keep rendering on the map (Law 3 lifecycle recolour) but leave the
 *     count: the wall answers "is anything red", and acked = steel by law.
 *   • OC FIRES — ambient context count from the hero map layer. (The #268
 *     CAMERAS chip left with the camera field: PART XI Ruling 1.2 removed
 *     Live Video Feeds from the wall map, so its "hero map layer count" has
 *     no source — a hard-0 chip would be dishonest. Cameras live in the
 *     flanks as pictures.)
 *   • Standing-alarm line (Part II §5): "N unacknowledged · oldest Xh —
 *     triage mode" when the EEMUA ceiling/shift rule trips.
 *
 * `WallChips` is presentational + pure-input (unit-testable); `WallChipStrip`
 * wires the shared wall queue sweep.
 */

export interface WallChipMapCounts {
  fire?: number;
}

/** Presentational strip — callers inject the queue items + map counts. */
export function WallChips({
  items,
  mapCounts,
  nowMs,
}: {
  items: QueueItem[];
  mapCounts: WallChipMapCounts;
  nowMs: number;
}): JSX.Element {
  const urgent = items.filter(isUnackedUrgent).length;
  const standing = standingAlarmStatus(items, nowMs);
  return (
    <div className="pointer-events-none flex flex-col items-start gap-1.5" data-testid="wall-chips">
      <div className="flex items-center gap-1.5">
        <WallChip label="Urgent" value={urgent} tone={urgent > 0 ? 'urgent' : 'quiet'} />
        <WallChip label="OC Fires" value={mapCounts.fire ?? 0} tone="fire" />
      </div>
      {/* Standing-alarm-ceiling health line (>10 unacked / >1 shift, Part II §5). */}
      {standing && (
        <div
          data-testid="wall-standing-alarm"
          role="status"
          className="flex items-center gap-1.5 rounded border border-red-800/70 bg-red-900/70 px-2 py-0.5 text-xs font-semibold text-red-100 backdrop-blur"
        >
          <span aria-hidden>⚠</span>
          <span>
            {standing.count} unacknowledged · oldest {standing.oldestHours}h — triage mode
          </span>
        </div>
      )}
    </div>
  );
}

/** Container: the shared wall queue sweep + a render-time clock (re-renders
 *  arrive on the wall cadence, so the standing-alarm age stays current). */
export function WallChipStrip({ mapCounts }: { mapCounts: WallChipMapCounts }): JSX.Element {
  const items = useWallQueueItems();
  return <WallChips items={items} mapCounts={mapCounts} nowMs={Date.now()} />;
}

type ChipTone = 'urgent' | 'fire' | 'neutral' | 'quiet';

const CHIP_TONE: Record<ChipTone, string> = {
  // URGENT-RED (Law 1) — the only red on the chips, matching the map alarm hue.
  urgent: 'border-[#ff3b30] text-[#ff3b30]',
  // OC-FUL flame (Law 2c).
  fire: 'border-[#e25822] text-[#e8956a]',
  neutral: 'border-slate-600 text-slate-200',
  // Quiet room is the success state (Part I) — zero urgents reads calm, not alarm.
  quiet: 'border-slate-700 text-slate-400',
};

function WallChip({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: ChipTone;
}): JSX.Element {
  return (
    <div
      className={`flex items-baseline gap-2 rounded-lg border-2 bg-slate-950/80 px-2.5 py-1 backdrop-blur ${CHIP_TONE[tone]}`}
      data-testid={`wall-chip-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">
        {label}
      </span>
      <span className="text-xl font-bold tabular-nums leading-none">{value}</span>
    </div>
  );
}
